import Instrument from './Instrument.js';
import Slideable from "instruments/Slideable.js";
import {isNumber} from 'utils/IsNumber.js';
import SamplesStream from 'track/SamplesStream.js';
import {crop} from 'engine/Crop.js';
import ethereum from 'decentralized/ethereum.js';
import SimpleSampler from './SimpleSampler.js';
import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import {rotate} from 'utils/rotateArray.js';
import SamplerVoices from './SamplerVoices.js';
import Notes from 'midi/Notes.js';
import Sample from 'repos/Sample.js';
import {getNoteSequence} from 'tensorflow/Midifier.js';
import Tone from 'tone';
import SimpleResampler from 'track/SimpleResampler.js';
import {SAMPLED_INSTRUMENT} from 'constants/instrument-types.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {
    setTrackIsLoading, 
} from 'actions/index.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {CLOSEST, OCTAVE} from 'constants/interpolation-modes.js';
import {ALTERNATE, MIX, ROUND_ROBIN} from 'constants/instrument-mix-modes.js';
import {findClosest, findClosestByOctave} from './interpolate.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import OptionParameter from 'parameters/OptionParameter.js';
import KeyScaled from './KeyScaled.js';
import {findLoop} from 'utils/findLoop.js';

const NUM_VOICES = 8;

class SampledInstrument extends Slideable((KeyScaled(Instrument))) {
    constructor(sequencerEngine, mixSession, context, track) {
        super();
        
        this.cache = {};
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context;
        this.track = track;
        this.solutionNumber = 0;

        this.outputNode = new Tone.Gain(1);
        this.voices = new SamplerVoices(NUM_VOICES, this.outputNode, this);

        this.resampler = new SimpleResampler();

        // map from transpose => sampler
        // sampler may contain baseTranspose that tunes it correctly
        this.samplers = {};

        this.interpolationModeParameter = new AbstractParameter(
            'interpolationMode', this,
            (o, x) => [],
            {
                min: CLOSEST,
                max: OCTAVE,
                unit: '',
                defaultValue: OCTAVE,
                automatable: false
            });

        this.registerParameter(this.interpolationModeParameter);

        this.mixModeParameter = new OptionParameter(
            'mixMode', this,
            (o, x) => [],
            {
                min: 0,
                prettyName: "Mix Mode",
                options: {
                    0: ROUND_ROBIN,
                    0.5: MIX,
                    1: ALTERNATE
                },
                max: 1,
                unit: '',
                defaultValue: ROUND_ROBIN,
                uxAutomatable: false
            });

        this.registerParameter(this.mixModeParameter);

        this.monophonicParameter = new AbstractParameter(
            'monophonic', this,
            (o, x) => {
                this.voices.numVoices = x ? 1 : NUM_VOICES;
            },
            {
                min: false,
                max: true,
                unit: '',
                defaultValue: false,
                automatable: false,
            });

        this.registerParameter(this.monophonicParameter);

        this.crossFadeSignal = new Tone.Signal(0);
        this.crossFadeParameter = new AudioParamValueParameter(
            'crossFade',
            this.crossFadeSignal,
            {min: 0,
             max: 1,
             defaultValue: 0,
             automationRate: A_RATE,
            });
        this.registerParameter(this.crossFadeParameter);

        this.detuneSignal = new Tone.Signal(1000);
        this.detuneParameter = new AudioParamValueParameter(
            'detune',
            this.detuneSignal,
            {min: -10000,
             max: 10000,
             defaultValue: 0,
             automationRate: A_RATE,
            });
        this.registerParameter(this.detuneParameter);

        this.setupLoopParameters();
        this.setupAmpEnvelopeParameters();

        this.smoothParameter = new AbstractParameter(
            'smooth', this,
            (o, x) => {
                let min = 1 - x;
                this.voices.voices.forEach(
                    voice => voice.ampLFO.smooth = min);
            },
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: false,
                uxAutomatable: false
            });

        this.registerParameter(this.smoothParameter);

        this.glideParameter = new AbstractParameter(
            'glide', this,
            (o, x) => [],
            {
                min: 0,
                max: 1000,

                unit: '',
                defaultValue: 0,
            });

        this.registerParameter(this.glideParameter);

        this.lastTranspose = undefined;

        this.triggerCounters= {};
        this.loaded = true;

        this.getParameters().forEach(
            x => x.isLoaded = true);

        this.voices.setCrossFade(this.crossFadeSignal);

        this.glideTransposes = {};
        this.setupSlide();
    } 

    get allSamplers() {
        let samplers = [];
        for (let transpose in this.samplers) {
            samplers = [... samplers, ... this.samplers[transpose]];
        }
        return samplers;
    }

    onAttackChanged(attack) {
        for (let transpose in this.ampEnvelopes) {
            this.ampEnvelopes[transpose].attack = attack / 1000.0;
        }
    }

    onDecayChanged(decay) {
        for (let transpose in this.ampEnvelopes) {
            this.ampEnvelopes[transpose].decay  = decay / 1000.0;
        }
    }

    onSustainChanged(sustain) {
        for (let transpose in this.ampEnvelopes) {
            this.ampEnvelopes[transpose].sustain = Math.min(0.99, sustain);
        }
    }

    onReleaseChanged(release) {
        for (let transpose in this.ampEnvelopes) {
            this.ampEnvelopes[transpose].release = release / 1000.0;
        }
    }

    get loop() {
        return this.allSamplers.every(s => s.loop);
    }

    set loop(loop) {
        this.allSamplers.forEach(s => s.loop = loop);
        /*
        this.voices.voices.forEach(
            voice => voice.ampLFO.loop = loop);
            */
    }

    get ampEnvelopes() {
        return this.voices.ampEnvelopes;
    }

    setupAmpEnvelopeParameters() {
        this.ampAttackParameter = new AbstractParameter(
            'Attack', this,
            (o, x) => {
                this.onAttackChanged(x);
            },
            {
                min: 0,
                max: 5000,
                unit: '',
                defaultValue: 10,
            });

        this.registerParameter(this.ampAttackParameter);

        this.ampDecayParameter = new AbstractParameter(
            'Decay', this,
            (o, x) => {
                for (let transpose in this.ampEnvelopes) {
                    this.ampEnvelopes[transpose].decay = x / 1000.0;
                }
            },
            {
                min: 0,
                max: 5000,
                unit: '',
                defaultValue: 10,
            });

        this.registerParameter(this.ampDecayParameter);

        this.ampSustainParameter = new AbstractParameter(
            'Sustain', this,
            (o, x) => {
                this.onSustainChanged(x);
            },
            {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.8,
            });

        this.registerParameter(this.ampSustainParameter);

        this.ampReleaseParameter = new AbstractParameter(
            'Release', this,
            (o, x) => {
                this.onReleaseChanged(x);
            },
            {
                min: 0,
                max: 5000,
                unit: '',
                defaultValue: 10,
            });

        this.registerParameter(this.ampReleaseParameter);

        this.attackParameter = this.ampAttackParameter;
        this.decayParameter = this.ampDecayParameter;
        this.sustainParameter = this.ampSustainParameter;
        this.releaseParameter = this.ampReleaseParameter;
    }

    setupLoopParameters() {
        this.loopParameter = new AbstractParameter(
            'loop', this,
            (o, x) => {
                this.loop = x;
            },
            {
                min: false,
                max: true,
                unit: '',
                defaultValue: false,
            });

        this.registerParameter(this.loopParameter);

        this.loopStartParameter = new AbstractParameter(
            'loopStart', this,
            (o, x) => {
                Array.from(this.voices.samplersUsed)
                    .forEach(
                        s => s.setLoopPoints(
                            {
                                loopStart: x,
                                loopLength: this.loopLength
                            }));
            },
            {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: 0,
            });

        this.registerParameter(this.loopStartParameter);

        this.loopLengthParameter = new AbstractParameter(
            'loopLength', this,
            (o, x) => {
                Array.from(this.voices.samplersUsed)
                    .forEach(
                        s => s.setLoopPoints(
                            {
                                loopStart: this.loopStart,
                                loopLength: x,
                            }));
            },
            {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: 100,
            });

        this.registerParameter(this.loopLengthParameter);
    }

    get loopStart() {
        return this.loopStartParameter.getAtStep(this.currentStep);
    }

    get loopLength() {
        return this.loopLengthParameter.getAtStep(this.currentStep);
    }

    onNewStep(step, time) {
        this.currentStep = step;
        if (this.oldMixMode !== this.mixMode) {
            this.cache = {};
        }
        this.oldMixMode = this.mixMode;

        this.onAttackChanged(this.ampAttack);
        this.onDecayChanged(this.ampDecay);
        this.onSustainChanged(this.ampSustain);
        this.onReleaseChanged(this.ampRelease);

        if (!this.loop ||
            ((!this.loopStartParameter.isConnectedToAnyModulator() && 
             !this.loopLengthParameter.isConnectedToAnyModulator()) &&
            (this.loopStartParameter.automations.length === 0 &&
             this.loopLengthParameter.automations.length === 0))) {
            // we don't need to update loop start/end points
            return;
        }

        // update smooth
        let min = 1 - this.smoothParameter.get();
        this.voices.voices
            .filter(
                voice => voice.ampLFO.lfo)
            .forEach(
                voice => voice.ampLFO.lfo.min = min);

        let loopStart = this.loopStart;
        let loopLength = this.loopLength;
        Tone.Master.context.setTimeout(() => {
            Array.from(this.voices.samplersUsed)
                .forEach(
                    s => s.setLoopPoints({
                        loopStart: loopStart,
                        loopLength: loopLength,
                    }));
        }, 1.0 / Tone.Master.context.sampleRate);
    }

    get glide() {
        return this.glideParameter.getAtStep(this.trackStep);
    }

    set glide(g) {
        this.glideParameter.set(g);
    }

    get interpolationMode() {
        return this.interpolationModeParameter.getAtStep(this.trackStep);
    }

    set interpolationMode(m) {
        this.interpolationModeParameter.set(m);
        this.cache = {};
    }

    get monophonic() {
        return this.monophonicParameter.get();
    }

    set monophonic(m) {
        this.monophonicParameter.set(m);
    }

    get mixMode() {
        return this.mixModeParameter.get();
    }

    set mixMode(m) {
        this.mixModeParameter.set(m);
        this.cache = {};
    }

    get trackStep() {
        return this.sequencerEngine.trackStep;
    }

    get ampAttack() {
        return this.ampAttackParameter.getAtStep(this.currentStep);
    }

    get ampDecay() {
        return this.ampDecayParameter.getAtStep(this.currentStep);
    }

    get ampSustain() {
        return this.ampSustainParameter.getAtStep(this.currentStep);
    }

    get ampRelease() {
        return this.ampReleaseParameter.getAtStep(this.currentStep);
    }

    connect(node) {
        this.outputNode.connect(node);
    }

    disconnect() {
        this.outputNode.disconnect();
    }

    connectToTrack(track) {
        if (!track) {
            this.connectedTrack = undefined;
            this.resampler.input = undefined;
            return;
        }
        // we're gonna sample from a track
        this.connectedTrack = track;
        this.resampler.input = track.volumeNode;
    }

    getInstrument() {
        return this;
    }

    getInstrumentType() {
        return SAMPLED_INSTRUMENT;
    }

    getName() {
        return "Sampled Instrument";
    }

    getPitchEnvelope(transpose) {
        if (this.glide === 0) {
            return undefined;
        }
        let lastTranspose = this.lastTranspose === undefined ? transpose : this.lastTranspose;
        let min = 0;
        let max = (transpose - lastTranspose) * 100;
        let pitchEnvelope = 
            new Tone.ScaledEnvelope({
	    "attack" : this.glide / 1000.0,
	    "decay" : 2,
            "min": min,
	    "max" : max,
            "sustain": 1,
	    "release" : 0.001,
            "curve": "linear"
            });
        pitchEnvelope._releaseCurve = 'linear';
        pitchEnvelope._decayCurve = 'linear';
        return pitchEnvelope;
    }

    triggerAttack(transpose=0, time, velocity=1, start=0, stepData) {
        let {samplers, closest} = this.getSampler(transpose, false);
        if (samplers === undefined) {
            return;
        }

        if (!time) {
            time = new Tone.Time(Tone.Master.context.currentTime);
        }
        this.scaleByKey(time, transpose);
        this.setEnvelopes(stepData, transpose);
        
        let pitchEnvelope = this.getPitchEnvelope(transpose);
        if (this.monophonic) {
            this.releaseAll(time);
        }
        for (let i in samplers) {
            samplers[i].sampler.attack = this.ampAttackParameter.getKeyScaledValue(transpose) / 1000.0;
            samplers[i].sampler.release = Math.max(4, this.ampReleaseParameter.getKeyScaledValue(transpose) / 1000.0);
            this.voices.triggerAttack(
                samplers[i],
                this.glide !== 0 && this.lastTranspose !== undefined ? this.lastTranspose - closest : transpose - closest,
                time,
                velocity,
                this.getLoopPoints(start, samplers[i]),
                pitchEnvelope);
        }
        this.glideTransposes[transpose] = this.lastTranspose;
        this.lastTranspose = parseFloat(transpose);
    }

    playSampler(sampler) {
        this.voices.triggerAttack(
            sampler,
            0,
            0,
            1,
            0,
            undefined);
    }

    async triggerAttackRelease(transpose, duration, time, velocity, start, stepData) {
        let {samplers, closest} = this.getSampler(transpose, true);
        if (samplers === undefined) {
            return;
        }

        this.scaleByKey(time, transpose);
        this.setEnvelopes(stepData, transpose);
        
        if (this.monophonic && !stepData.slide) {
            this.releaseAll(time);
        }
        /*
        if (this.lastSlideTranspose !== undefined) {
            this.releaseSlide(time);
        }
        if (stepData.slide) {
            this.triggerSlide(transpose, time, duration);
            return;
        } else if (this.lastSlideTranspose !== undefined) {
            this.triggerSlide(transpose, time, duration, false);
        }
        if (time) {
            let detune = this.detuneParameter.get(true);
            this.detuneSignal.setValueAtTime(detune, time-0.001);
        } 

        this.lastSlideTranspose = undefined;
        this.lastSlideMax = undefined;
        */

        let pitchEnvelope = this.getPitchEnvelope(transpose);

        for (let i in samplers) {
            let sampler = samplers[i];
            sampler.sampler.release = this.ampReleaseParameter.getKeyScaledValue(transpose) / 1000.0;
            sampler.sampler.attack = this.ampAttackParameter.getKeyScaledValue(transpose) / 1000.0;
            this.voices.triggerAttackRelease(
                sampler,
                this.glide !== 0 && this.lastTranspose !== undefined ? this.lastTranspose - closest : transpose - closest,
                duration,
                time,
                velocity,
                this.getLoopPoints(start, sampler),
                pitchEnvelope,
                start);
        }
        this.glideTransposes[transpose] = this.lastTranspose;
        this.lastTranspose = parseFloat(transpose);
    }

    getLoopPoints(start, sampler) {
        return {
            start,
            loopStart: this.loopStart,
            loopLength: this.loopLength,
            loopLengthSeconds: (sampler.endTime - sampler.startTime) * (this.loopLength / 100),
        };
    }

    async triggerRelease(transposes=[0]) {
        // reset the release parameter
        this.releaseParameter.set(this.releaseParameter.get(), true, true);
        for (let i in transposes) {
            let transpose = transposes[i];
            let {samplers, closest} = this.getSampler(transpose, true);
            if (samplers === undefined) {
                continue;
            }
            let _transpose = this.glide !== 0 ? this.glideTransposes[transpose] || transpose :
                transpose;
            for (let i in samplers) {
                let sampler = samplers[i];
                samplers[i].sampler.release = this.ampRelease / 1000.0;
                this.voices.triggerRelease(
                    sampler,
                    [_transpose - closest]);
            }
        }
    }

    releaseAll(time) {
        this.voices.clear();
        if (!time) {
            this.clearSlide();
            this.detuneSignal.value = this.detuneParameter.get(true);
            this.lastTranspose = 0;
        }
        for (let transpose in this.samplers) {
            let samplers = this.samplers[transpose];
            samplers.forEach(sampler => {
                sampler.get(0).releaseAll(time || new Tone.Time(Tone.Master.context.currentTime), 0.01);
            });
        }
    }

    newSample(transpose, sample, samplesStream) {
        return new Promise(async (resolve, reject) => {
            if (typeof sample === 'string') {
                sample = new Sample(sample);
            } 
            sample = sample.getCopy();
            this.track.samplesStream = samplesStream || new SamplesStream(sample, this.sequencerEngine);

            if (sample.audioBuffer === undefined) {
                await sample.load();
            }

            let samplerData = {
                slices: undefined,
                offset: 0,
                transpose: 0,
                volume: 0
            };
            
            // create sampler
            let sampler = new SimpleSampler(
                this.track, sample, () => [], undefined, samplerData);
            sampler.samplesStream = samplesStream;
            
            setTimeout(async () => {
                sampler.init();
                this.detuneSignal.connect(sampler.detuneSignal.input);
                this.detuneParameter.set(this.detuneParameter.get());

                if (transpose === undefined) {
                    let sampleTranspose = await this.getPitch(sample);
                    if (sampleTranspose === undefined) {
                        // dont mess with transpose if we couldnt get
                        // pitch information
                        if (transpose === undefined) {
                            transpose = 0;
                        } 
                    } else {
                        if (transpose === undefined) {
                            transpose = sampleTranspose;
                        }
                        let transposeToSet = transpose - sampleTranspose;
                        if (transposeToSet < -30) {
                            transposeToSet += 36;
                        }
                        sampler.transpose = transposeToSet;
                    }
                } else if (transpose !== undefined) {
                    // sampler.transpose = 0;
                }

                // clear the cache 
                this.cache = {};

                if (this.samplers[transpose] === undefined) {
                    this.samplers[transpose] = [];
                }
                this.samplers[transpose] = [... this.samplers[transpose], sampler];
                this.selectSampler(transpose, sampler);
                resolve({sampler: sampler, samplers: this.samplers[transpose]});
            }, 500);
       });
    }

    getTriggerCount(transpose) {
        return this.triggerCounters[transpose] || 0;
    }

    get numberOfSamplers() {
        let len = 0;
        for (let i in this.samplers) {
            len += this.samplers[i].length;
        }
        return len;
    }

    get defaultSampler() {
        if (this.samplers === undefined) {
            return undefined;
        }
        let defaultKey = Object.keys(this.samplers)[0];
        if (defaultKey === undefined) {
            return undefined;
        }

        let s = this.samplers[defaultKey][0];
        return s;
    }

    getAllSamplersAtExactTranspose(transpose) {
        let samplers = this.samplers[transpose];
        if (this.mixMode === ALTERNATE) {
            samplers = this.samplers[transpose].filter(s => s.on);
        } 
        return samplers;
    }

    filterNeededSamplers(samplers, transpose, doRotate=false) {
        if (samplers === undefined) {
            return undefined;
        }
        if (this.mixMode === ROUND_ROBIN) {
            let samplersToSend = [samplers[this.getTriggerCount(transpose) % samplers.length]];
            if (doRotate) {
                this.triggerCounters[transpose] = this.getTriggerCount(transpose) + 1;
            }
            return samplersToSend;
        }
        return samplers;
    }

    getSampler(transpose, doRotate=false) {
        if (transpose in this.cache) {
            let cached = this.cache[transpose];
            return {
                ... cached,
                samplers: this.filterNeededSamplers(
                    cached.samplers, transpose, doRotate),
            };
        }
        let closest = transpose;
        if (! (transpose in this.samplers)) {
            closest = this.findClosest(transpose);
        }

        let samplers = this.getAllSamplersAtExactTranspose(closest);
        this.cache[transpose] = {
            closest: parseFloat(closest),
            samplers: samplers,
        };
        return {
            closest: parseFloat(closest),
            samplers: this.filterNeededSamplers(samplers, transpose, doRotate),
        };
    }

    findClosest(transpose) {
        if (this.interpolationMode === CLOSEST) {
            return findClosest(transpose, this.samplers);
        } else {
            return findClosestByOctave(transpose, this.samplers);
        }
    }

    getJson() {
        let json = {
            ... super.getJson(),
            ampAttack: this.ampAttack,
            ampDecay: this.ampDecay,
            ampSustain: this.ampSustain,
            ampRelease: this.ampRelease,
            crossFade: this.crossFadeParameter.get(),
            interpolationMode: this.interpolationMode,
            mixMode: this.mixMode,
            samplers: {},
            glide: this.glide,
            monophonic: this.monophonic,
            detune: this.detuneParameter.get(),
            loopStart: this.loopStart,
            loopLength: this.loopLength,
            loop: this.loopParameter.get(),
            smooth: this.smoothParameter.get(),
        };

        for (let transpose in this.samplers) {
            json.samplers[transpose] =
                this.samplers[transpose].map(s => s.getJson());
        }
        
        json = saveParameterToJson(json, 'ampAttackParameter', this.ampAttackParameter);
        json = saveParameterToJson(json, 'ampDecayParameter', this.ampDecayParameter);
        json = saveParameterToJson(json, 'ampSustainParameter', this.ampSustainParameter);
        json = saveParameterToJson(json, 'ampReleaseParameter', this.ampReleaseParameter);
        json = saveParameterToJson(json, 'interpolationModeParameter', this.interpolationModeParameter);
        json = saveParameterToJson(json, 'mixModeParameter', this.mixModeParameter);
        json = saveParameterToJson(json, 'glideParameter', this.glideParameter);
        json = saveParameterToJson(json, 'monophonicParameter', this.monophonicParameter);
        json = saveParameterToJson(json, 'detuneParameter', this.detuneParameter);
        json = saveParameterToJson(json, 'loopStartParameter', this.loopStartParameter);
        json = saveParameterToJson(json, 'loopLengthParameter', this.loopLengthParameter);
        json = saveParameterToJson(json, 'smoothParameter', this.smoothParameter);
        json = saveParameterToJson(json, 'loopParameter', this.loopParameter);
        json = saveParameterToJson(json, 'crossFadeParameter', this.crossFadeParameter);
        return json;
    }

    fromJsonSync(json, samplers, isPreset=false) {
        if (isPreset) {
            this.clearSamples();
        }
        this.ampAttackParameter.set(json.ampAttack);
        this.ampDecayParameter.set(json.ampDecay);
        this.ampSustainParameter.set(json.ampSustain);
        this.ampReleaseParameter.set(json.ampRelease);

        if (json.loopStart !== undefined) {
            this.loopStartParameter.set(json.loopStart);
        }

        if (json.loopLength !== undefined) {
            this.loopLengthParameter.set(json.loopLength);
        }

        if (json.loop !== undefined) {
            this.loopParameter.set(json.loop);
        }

        if (json.crossFade !== undefined) {
            this.crossFadeParameter.set(json.crossFade);
        }
        
        if (json.interpolationMode) {
            this.interpolationMode = json.interpolationMode;
        }
        if (json.mixMode !== undefined) {
            this.mixMode = isNumber(json.mixMode) ? ROUND_ROBIN : json.mixMode;
        }

        if (json.glide !== undefined) {
            this.glide = json.glide;
        }

        if (json.monophonic !== undefined) {
            this.monophonic = json.monophonic;
        }

        if (json.smooth !== undefined) {
            this.smoothParameter.set(json.smooth);
        }

        loadParameterFromJson(json, 'ampAttackParameter', this.ampAttackParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'ampDecayParameter', this.ampDecayParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'ampSustainParameter', this.ampSustainParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'ampReleaseParameter', this.ampReleaseParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'interpolationModeParameter', this.interpolationModeParameter);
        loadParameterFromJson(json, 'mixModeParameter', this.mixModeParameter);
        loadParameterFromJson(json, 'glideParameter', this.glideParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'monophonicParameter', this.monophonicParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'detuneParameter', this.detuneParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'loopStartParameter', this.loopStartParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'loopLengthParameter', this.loopLengthParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'loopParameter', this.loopParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'smoothParameter', this.smoothParameter, this.sequencerEngine);
        loadParameterFromJson(json, 'crossFadeParameter', this.crossFadeParameter, this.sequencerEngine);

        if (samplers) {
            for (let transpose in samplers) {
                let _samplers = samplers[transpose];
                for (let i in _samplers) {
                    let sample = _samplers[i].sample;
                    let samplerJson = json.samplers[transpose][i];
                    this.setSampleForTranspose(transpose, sample.getCopy(true), samplerJson, _samplers[i].tempo);
                }
            }
        } 
        this.samples = {... this.samplers};

        this.loop = this.loop;

        if (json.detune !== undefined) {
            this.detuneParameter.set(json.detune);
        } else {
            this.detuneParameter.set(0);
        }
    }

    async fromJson(json, ignoreId, isPreset=false) {
        this.loaded = false;
        this.store.dispatch(setTrackIsLoading(this.track.subSequence.id, this.track.trackNumber, true));
        this.getParameters().forEach(
            x => x.isLoaded = false);
        this.fromJsonSync(json, undefined, isPreset);
        let promises = [];
        for (let transpose in json.samplers) {
            if (json.samplers[transpose].length !== undefined) {
                for (let i in json.samplers[transpose]) {
                    let sampler = json.samplers[transpose][i];
                    promises.push(this.loadSamplerFromJson(parseFloat(transpose), sampler));
                }
            } else {
                promises.push(this.loadSamplerFromJson(parseFloat(transpose), json.samplers[transpose]));
            }
        }
        await Promise.all(promises);
        this.loaded = true;
        this.track.sample = this.defaultSampler && this.defaultSampler.sample;
        if (this.defaultSampler) {
            await this.defaultSampler.samplesStream.fetch();
            this.track.samplesStream = this.defaultSampler.samplesStream;
        }
        this.getParameters().forEach(
            x => x.isLoaded = true);
        this.store.dispatch(setTrackIsLoading(this.track.subSequence.id, this.track.trackNumber, false));
   }

    setSampleForTranspose(transpose, sample, samplerJson, tempo) {
        let sampler = new SimpleSampler(this.track, sample, () => [], samplerJson.slices, samplerJson);
        if (tempo !== undefined) {
            sampler.tempo = tempo;
        }
        sampler.init(undefined, undefined, true);
        this.detuneSignal.connect(sampler.detuneSignal, 0, 0);
        if (this.samplers[transpose] === undefined) {
            this.samplers[transpose] = [];
        }
        this.samplers[transpose].push(sampler);

        this.cache = {};
    }

    async loadSamplerFromJson(transpose, samplerJson) {
        let sample = new Sample(samplerJson.sampleId, this.sequencerEngine.encryptedIpfs);
        try {
            await sample.load();
        } catch (e) {
            return;
        }

        this.setSampleForTranspose(transpose, sample, samplerJson);
   }

    clearSamples() {
        for (let i in this.samplers) {
            for (let t in this.samplers[i]) {
                this.samplers[i][t].dispose();
            }
            delete this.samplers[i];
        }
        this.voices.clear();

        // get rid of all modulations
        this.ripMods();
    }

    getSamples() {
        let samples = [];
        for (let i in this.samplers) {
            for (let t in this.samplers[i]) {
                samples.push(this.samplers[i][t].sample);
            }
        }
        return samples;
    }

    ripMods() {
        if (this.parameters === undefined) {
            return;
        }
        for (let parameter of this.parameters) {
            if (parameter.object && parameter.isConnectedToAnyModulator()) {
                parameter.disconnectFromAllModulators();
            }
        }
    }

    dispose() {
        this.clearSamples();
        this.voices.dispose();
        this.voices = undefined;
        this.samplers = undefined;
        this.outputNode.dispose();
        this.outputNode = undefined;
        this.parameters = undefined;
    }

    getPitch(sample, startTime=0) {
        const middleC = 48;
        return new Promise(async (resolve, reject) => {
            let nameNote = this.getNoteFromName(sample.name);
            if (nameNote !== undefined) {
                resolve(nameNote);
                return;
            }
            resolve(undefined);
            return;
            let buffer = sample.audioBuffer;
            buffer = crop(buffer, startTime, startTime + Math.min(40000, buffer.length - startTime), Tone.context, 10000);
            let noteSequence = await getNoteSequence(buffer);
            if (noteSequence.notes.length > 0) {
                noteSequence.notes.sort((a,b) => (b.endTime - b.starTime) - (a.endTime - a.startTime));
                let transpose = noteSequence.notes[0].pitch - middleC;
                resolve(transpose);
            }
            resolve(undefined);
        });
    }

    deleteSampler(sampler) {
        let transpose = undefined;
        for (let t in this.samplers) {
            let index = this.samplers[t].indexOf(sampler);
            if (index > -1) {
                this.samplers[t].splice(index, 1);
                sampler.dispose();
            }
            if (this.samplers[t].length === 0) {
                delete this.samplers[t];
            }
        }

        this.cache = {};
        this.voices.clear();
    }

    selectSampler(transpose, sampler) {
        this.cache = {};
        this.samplers[transpose]
            .filter(s => s !== sampler)
            .forEach(s => s.on = false);
        sampler.on = true;
    }

    getNoteFromName(name) {
        let firstLetter = undefined;
        let matched = name.match(/([A-G]||[a-g])([#b])?\d/)
        if (!matched || matched[0].length < 2) {
            /*
            matched = name.match(/\d\d/)
            console.log('matched = ', matched);
            if (!matched) {
                return undefined;
            } else {
                name = new Tone.Frequency(parseInt(matched[0]), "midi").toNote();
            }
            */
            return undefined;
        } else {
            name = matched[0].toUpperCase();
        }


        if (name.length === 2) {
            firstLetter = name.substring(0, 1);
        } else if (name.length === 3) {
            firstLetter = name.substring(0, 2);
        }
        if (firstLetter &&
            Notes.baseNotes.indexOf(firstLetter) > -1) {
            let x = Notes.fromString(name).getTranspose();
            return x;
        }

        return undefined;
    }

    async findLoop(i, goal, minSize) {
        if (i === undefined) {
            this.solutionNumber += 500;
            i = this.solutionNumber;
        }
        let sampler = this.getAllSamplersAtExactTranspose(0)[0];
        let {start, end} = findLoop(sampler.audioBuffer, i, goal, minSize);
        if (end < start) {
            this.solutionNumber = Math.ceil(Math.random() * 10);
            this.findLoop(undefined, goal, minSize);
            return;
        }
        
        let loopStart = 100.0 * start / (Tone.Master.context.sampleRate * sampler.getDuration());
        this.loopStartParameter.set(loopStart);
        this.loopLengthParameter.set(
            100.0 * (end - start) /
                (Tone.Master.context.sampleRate * sampler.getDuration()));
    }

    getCopy(context=this.context, track, sequencerEngine=this.sequencerEngine) {
        let instrument = new SampledInstrument(
            sequencerEngine, this.mixSession, context, track);

        instrument.fromJsonSync(this.getJson(), this.samplers);
        return instrument;
    }

    async makePublicIfNeeded() {
        for (let i in this.samplers) {
            let samplers = this.samplers[i];
            for (let j in samplers) {
                let sampler = this.samplers[i][j];
                let {sample} = sampler;
            
                let guildId = await ethereum.guildSamples.getGuildIdForSound(sample.getId());
                if (guildId !== undefined && guildId !== 0) {
                    let id = await ethereum.guildSamples.saveSample(sample);
                    sample.ipfsHash = id;
                } 
            }
        }
    }

    setEnvelopes(stepData, transpose) {
        let attack = this.ampAttackParameter.getKeyScaledValue(transpose);
        let decay = this.ampDecayParameter.getKeyScaledValue(transpose);
        let sustain = this.ampSustainParameter.getKeyScaledValue(transpose);
        let release = this.ampReleaseParameter.getKeyScaledValue(transpose);

        this.onAttackChanged(attack);
        this.onDecayChanged(decay);
        this.onSustainChanged(sustain);
        this.onReleaseChanged(release);

        if (stepData.releaseParameter.get() !== null) {
            this.releaseParameter.set(stepData.releaseParameter.get());
        }
    }
}

export default SampledInstrument;
